import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useLocation, useHistory, matchPath, Route } from 'react-router-dom';
import { IconButton, Toolbar, Typography, Box, Button } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material/';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEmpty } from '../../../utils/baseHelpers';
import { overrideDisabledRestaurants } from '../../../utils/flags';
import { useAppConfig } from '../../../utils/hooks/useAppConfig';
import { canEditAll, FEATURE_SCOPES, hasAccess } from '../../../utils/permissions';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import DisabledBanner from '../../DisabledBanner/DisabledBanner';
import HolidayHoursModal from '../../offers/Modals/HolidayHoursModal';
import LoyaltyModal from '../../offers/Modals/LoyaltyModal/LoyaltyModal';
import SettlementsBanner from '../../SettlementsBanner/SettlementsBanner';
import Loading from '../loading/Loading';
import { breadcrumbsPropTypes } from '../../../reducers/breadcrumbsReducer';
import routes from '../../../routes/Routes';
import LogoImage from '../../../assets/logoEatClub.svg';
import useStyles from './HeaderStyles';
import RestaurantPicker from '../../restaurantPicker/RestaurantPicker';
import PlatformPicker from '../../platformPicker/PlatformPicker';
import { userPropTypes } from '../../../data/models/User';
import { DateRangePicker } from '../../DateRangePicker/DateRangePicker';

/**
 * The standard app layout for most screens in the partner portal
 */
const AppHeader = ({
  breadcrumbs,
  children,
  showDrawer,
  premium = false,
  user,
  restaurantActive,
}) => {
  const appConfig = useAppConfig();
  const location = useLocation();
  const ref = useRef(null);
  const history = useHistory();

  const route = routes.find((routeToFind) => matchPath(location.pathname, routeToFind));
  const classes = useStyles({ route });

  // Whether the restaurant has loaded and is considered disabled
  const restaurantIsDisabled =
    restaurantActive?.objectId &&
    !restaurantActive?.enabled &&
    !overrideDisabledRestaurants.includes(restaurantActive?.objectId);

  // Whether we need to show the opt-in modal for loyalty
  const offerlessConfig = useSelector((state) => state.offerlessConfig);
  const holidayScheduleStatus = useSelector((state) => state.holidayScheduleStatus);
  const [isLoyaltyModalOpen, setIsLoyaltyModalOpen] = useState(false);
  const [isHolidayHoursModalOpen, setIsHolidayHoursModalOpen] = useState(false);

  // Show loyalty modal once offerless is loaded and not enabled
  useEffect(() => {
    if (
      !restaurantIsDisabled &&
      !offerlessConfig.fetching &&
      offerlessConfig.success &&
      !offerlessConfig.data?.offerlessEnabled &&
      appConfig.ecCountryCode === 'AU'
    ) {
      setIsLoyaltyModalOpen(true);
    }
  }, [offerlessConfig, restaurantActive?.objectId, restaurantIsDisabled, appConfig.ecCountryCode]);

  // Show holiday hours modal once loaded and not yet set
  // TODO remove this check in the new year
  useEffect(() => {
    if (
      !restaurantIsDisabled &&
      !holidayScheduleStatus.fetching &&
      holidayScheduleStatus.success &&
      !holidayScheduleStatus.data?.holidaysScheduled
    ) {
      // Also don't need to show once we're in the holiday period
      const date = dayjs();
      if (date.isBefore('2026-01-01')) {
        setIsHolidayHoursModalOpen(true);
      }
    }
  }, [
    holidayScheduleStatus,
    restaurantActive?.objectId,
    restaurantIsDisabled,
    appConfig.ecCountryCode,
  ]);

  const hideHeader = route?.hideHeader || (route?.premiumRequired && !premium);
  const hideMobileHeader = route?.hideMobileHeader || (route?.premiumRequired && !premium);
  const pageTitle = route?.pageTitle || null;
  const customPageTitle = route?.customPageTitle || null;
  const headerBackNavigation = route?.backNavigation;

  const hasBreadcrumbs = breadcrumbs?.data?.length > 0;
  const hasHeaderContent = pageTitle || headerBackNavigation || customPageTitle;

  const canEditDeals = hasAccess(user, FEATURE_SCOPES.OFFERS);

  // Better back functionality  (actually go back, rather than to a hardcoded screen)
  const previousRoute = useRef(route?.path);
  const currentRoute = useRef(route?.path);
  if (currentRoute.current !== route?.path) {
    previousRoute.current = currentRoute.current;
    currentRoute.current = route?.path;
  }

  const onBack = () => {
    // Try going back to the previous screen first
    if (headerBackNavigation?.goBack && previousRoute.current !== currentRoute.current) {
      currentRoute.current = previousRoute.current;
      history.push(currentRoute.current);
      return;
    }

    // If can't go back (e.g. loaded this page directly), use a default back route
    history.push(headerBackNavigation.route);
  };

  return (
    <>
      <Box className={classes.mobileHeader}>
        {!hideMobileHeader && (
          <IconButton color='inherit' aria-label='open drawer' edge='start' onClick={showDrawer}>
            <MenuIcon />
          </IconButton>
        )}
        <LogoImage className={classes.menuLogo} fill='#313131' />
      </Box>

      <Box className={classes.container}>
        <Loading />

        <Box className={classes.containerInner}>
          {!hideHeader && (
            <Box ref={ref} position='static' className={classes.appBar}>
              {/* Payment banner  */}
              {route?.showDebtBanner && <SettlementsBanner />}

              {route?.showDisabledBanner !== false &&
                (restaurantIsDisabled || restaurantActive?.closurePause) && <DisabledBanner />}

              <Toolbar className={classes.headerToolbar}>
                <Box className={classes.header}>
                  {/* Let pages override the headers */}
                  {!isEmpty(route?.customHeader)
                    ? route?.customHeader
                    : (hasBreadcrumbs || hasHeaderContent) && (
                        <Box style={{ width: '100%' }}>
                          {hasBreadcrumbs && (
                            <Box
                              className={classes.breadcrumbContainer}
                              style={{ marginBottom: '20px' }}
                            >
                              <Breadcrumbs />
                            </Box>
                          )}

                          {hasHeaderContent && (
                            <Box className={classes.titleContainer}>
                              <Box
                                style={{
                                  display: 'flex',
                                  gap: '24px',
                                  flexWrap: 'wrap',
                                  alignItems: 'center',
                                }}
                              >
                                {(customPageTitle && customPageTitle()) || (
                                  <Box style={{ display: 'flex', flexDirection: 'column' }}>
                                    {headerBackNavigation && (
                                      <Box
                                        className={classes.headerBackNavigation}
                                        style={{
                                          marginBottom: '20px',
                                          cursor: 'pointer',
                                          width: 'fit-content',
                                        }} // Add extra space if we're showing a title too
                                        onClick={onBack}
                                      >
                                        <svg
                                          width='13'
                                          height='12'
                                          viewBox='0 0 13 12'
                                          fill='none'
                                          xmlns='http://www.w3.org/2000/svg'
                                        >
                                          <path
                                            d='M6.5 0.5L1 6M1 6L6.5 11.5M1 6H12.5'
                                            stroke='black'
                                          />
                                        </svg>

                                        <Box className={classes.backNavigationLabel}>
                                          {headerBackNavigation?.label}
                                        </Box>
                                      </Box>
                                    )}

                                    {/* Title */}
                                    {route?.pageIcon && (
                                      <Box style={{ display: 'flex', marginBottom: '12px' }}>
                                        {route?.pageIcon}
                                      </Box>
                                    )}

                                    <Typography variant='h1' className={classes.title}>
                                      {pageTitle}
                                    </Typography>
                                  </Box>
                                )}

                                <Box
                                  style={{
                                    display: 'flex',
                                    gap: '8px',
                                    flexWrap: 'wrap',
                                    height: '40px',
                                  }}
                                >
                                  <Route exact path='*'>
                                    <RestaurantPicker />
                                  </Route>
                                  <Route
                                    exact
                                    path={['/analytics', '/menus', '/insights', '/transactions']}
                                  >
                                    <PlatformPicker />
                                  </Route>
                                </Box>
                              </Box>

                              <Box
                                style={{
                                  display: 'flex',
                                  gap: '8px',
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Route exact path={['/analytics', '/insights']}>
                                  <DateRangePicker
                                    options={[
                                      '7_days',
                                      '14_days',
                                      '1_month',
                                      '3_months',
                                      '1_year',
                                      'all_time',
                                    ]}
                                  />
                                </Route>
                                <Route exact path={['/settlements']}>
                                  {canEditAll(user) && (
                                    <Button
                                      color='secondary'
                                      variant='outlined'
                                      onClick={() => {
                                        history.push('/settlements/historical-statements');
                                      }}
                                    >
                                      Historical statements
                                    </Button>
                                  )}
                                  <Button
                                    color='secondary'
                                    variant='outlined'
                                    onClick={() => {
                                      history.push('/help-centre');
                                    }}
                                  >
                                    Need help?
                                  </Button>
                                </Route>
                                <Route exact path={['/offers/active']}>
                                  {canEditDeals && (
                                    <Button
                                      color='secondary'
                                      variant='outlined'
                                      onClick={() => {
                                        history.push('/offers/create');
                                      }}
                                      size='large'
                                      disabled={!restaurantActive?.enabled && !canEditAll(user)}
                                    >
                                      Create new
                                    </Button>
                                  )}
                                </Route>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                </Box>
              </Toolbar>
            </Box>
          )}
          {children}
        </Box>
      </Box>

      <LoyaltyModal
        isOpen={isLoyaltyModalOpen}
        onClose={() => {
          setIsLoyaltyModalOpen(false);
        }}
        onSave={() => {
          setIsLoyaltyModalOpen(false);
        }}
      />

      <HolidayHoursModal
        isOpen={!isLoyaltyModalOpen && isHolidayHoursModalOpen}
        onClose={() => {
          setIsHolidayHoursModalOpen(false);
        }}
        onSave={() => {
          setIsHolidayHoursModalOpen(false);
        }}
      />
    </>
  );
};

AppHeader.propTypes = {
  premium: PropTypes.bool,
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  showDrawer: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  user: userPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  premium: state.restaurantActive.restaurant?.premium,
  restaurantActive: state.restaurantActive.restaurant,
  breadcrumbs: state.breadcrumbs,
  user: state.user,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AppHeader);
